import { useState } from 'react';
import {
  STAGE_LABEL,
  canOpenView,
  matchesPerson,
  orderAssignees,
  seesEveryNote,
  STAGE_ORDER,
  type Kit,
  type NoteVisibility,
  type Stage,
} from '@/domain';
import { useBoard } from '@/application/usecases/useBoard';
import {
  useAssignKit,
  useKitAssignees,
  useNoteThread,
  useSendLabNote,
} from '@/application/usecases/useKits';
import { useAssigneeOrder } from '@/application/usecases/useAssigneeOrder';
import { useOfferUndo } from '@/features/shell/useOfferUndo';
import { AssigneeLine, LeastBusyToggle } from '@/ui/data/AssigneeLine';
import { useSession } from '@/application/usecases/useSession';
import { useAssignCases } from '@/application/usecases/useBulk';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';
import { LockIcon } from '@/ui/icons';
import styles from './AssignDialog.module.css';

/** Any bench but Ready, which only passing Final Check reaches. */
const DESTINATIONS = STAGE_ORDER.filter((stage) => stage !== 'ready');

export interface AssignDialogProps {
  kit: Kit;
  onClose: () => void;
}

/**
 * Give a case to one person. Section 1 of the request of 13 September 2026.
 *
 * Assign is the pair of Flag, and the pair only makes sense if the difference
 * is on screen: Flag moves the case to another department, Assign gives it to
 * a person inside the department it is already in and never changes the stage.
 * The lead line says so.
 *
 * The candidates come from the server, which applies its own bench rule; a
 * list filtered here would be a second copy of that rule. Leave it unassigned
 * is the first choice, the current assignee is marked, and when somebody is
 * timing the tray the dialog says so and still lets the case be given to
 * somebody else, because reassigning after the first person went home is the
 * normal reason to do it. Assigning does not start the clock; the scan does.
 *
 * Each person carries how many cases they hold now, and the list can be read
 * least busy first, so the work goes to whoever has room for it. Section 7 of
 * the request of 5 October 2026. The toast after it offers Undo, section 6.
 */
export function AssignDialog({ kit, onClose }: AssignDialogProps) {
  const { data: session } = useSession();
  /*
   * As Mark done does: the department first (where the case is, unless another
   * is chosen), then somebody in it, or anybody by searching. Request of
   * 5 October 2026. Another department moves the case there.
   */
  const [department, setDepartment] = useState<Stage>(kit.stage);
  const [search, setSearch] = useState('');
  const candidates = useKitAssignees(kit.id, department === kit.stage ? undefined : department);
  const assign = useAssignKit();
  const move = useAssignCases();
  const sendNote = useSendLabNote();
  const offerUndo = useOfferUndo();
  const { order, leastBusy, setLeastBusy } = useAssigneeOrder();
  const [choice, setChoice] = useState<string | null>(kit.assignedToUserId);
  const [error, setError] = useState<string | null>(null);
  // A note for Lab Notes with the assignment, and who sees it. Request of
  // 4 October 2026, section 7.
  const [note, setNote] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>('everyone');
  const [forward, setForward] = useState<string[]>([]);

  /*
   * Reassigning does not hand the new person what was written privately to the
   * one before: a private note follows the person, not the case. Management and
   * supervisors, who read every note, may forward chosen ones.
   */
  const seesAll =
    session !== undefined &&
    session !== null &&
    seesEveryNote({ permissions: session.permissions, isSupervisor: session.user.isSupervisor });
  const reassigning =
    kit.assignedToUserId !== null && choice !== null && choice !== kit.assignedToUserId;
  const thread = useNoteThread(seesAll && reassigning ? kit.id : undefined);
  const privateNotes = (thread.data?.items ?? []).filter(
    (item) => item.visibleTo !== null && item.visibleTo.id !== choice,
  );

  /*
   * Who is timing the tray right now comes from the board, the one place that
   * knows. It is asked for only by a caller the server would answer, the roles
   * that hold the production line or the overview: asking from a desk that
   * holds neither would be a refused request on every open, for a line that
   * would then be blank anyway.
   */
  const views = session?.views ?? [];
  const mayReadBoard =
    canOpenView(views, 'line') ||
    canOpenView(views, 'overview') ||
    session?.permissions.includes('admin') === true;
  const board = useBoard(mayReadBoard);
  const working = board.data?.liveWork.find((entry) => entry.kitId === kit.id) ?? null;

  const everybody = candidates.data ?? [];
  const ofDepartment = everybody.filter((person) => person.department === department);
  // The department's own people, or anybody by searching; then in the order asked for.
  const listed = orderAssignees(
    search.trim() !== ''
      ? everybody.filter((person) => matchesPerson(search, person))
      : ofDepartment.length > 0
        ? ofDepartment
        : everybody,
    order,
  );
  const moving = department !== kit.stage;
  const chosenName = everybody.find((person) => person.id === choice)?.name ?? null;

  function confirm() {
    setError(null);
    if (moving) {
      /*
       * To another department, and to the person there, in ONE command, so the
       * Undo on the toast takes the whole of it back: a move followed by a
       * separate assignment would leave an undo that only took the person off.
       * The note that came with it is written after, as a Lab Note; a note does
       * not change the case, so the undo stays open.
       */
      move.mutate(
        { kitIds: [kit.id], stage: department, userId: choice },
        {
          onSuccess: (result) => {
            const outcome = result.results[0];
            if (outcome?.ok !== true) {
              setError(outcome?.detail ?? strings.errors.server);
              return;
            }
            if (choice !== null && note.trim() !== '') {
              sendNote.mutate({
                kitId: kit.id,
                text: note.trim(),
                requestId: crypto.randomUUID(),
                visibleToUserId: visibility === 'assignee' ? choice : null,
              });
            }
            offerUndo(
              strings.assign.moved(kit.jobOrderNo, STAGE_LABEL[department], chosenName),
              result.undo,
            );
            onClose();
          },
          onError: (failure: Error) => setError(failureWords(failure)),
        },
      );
      return;
    }
    assign.mutate(
      {
        kitId: kit.id,
        userId: choice,
        ...(note.trim() === '' ? {} : { note: note.trim(), noteVisibility: visibility }),
        ...(reassigning && forward.length > 0 ? { forwardNoteIds: forward } : {}),
      },
      {
        onSuccess: (updated) => {
          offerUndo(
            updated.assignedToUserName === null
              ? `${kit.jobOrderNo} ${strings.assign.cleared}`
              : `${kit.jobOrderNo} ${strings.assign.assigned} ${updated.assignedToUserName}`,
            updated.undo,
            'neutral',
          );
          onClose();
        },
        // The server's words: who does not work where, or who is not in the system.
        onError: (failure: Error) => setError(failureWords(failure)),
      },
    );
  }

  const busy = assign.isPending || move.isPending;

  return (
    <ConfirmSheet
      title={`${strings.assign.title} ${kit.jobOrderNo}`}
      statement={
        moving
          ? strings.assign.moveStatement(kit.jobOrderNo, STAGE_LABEL[department], chosenName)
          : strings.confirm.assign(kit.jobOrderNo, choice === null ? null : chosenName)
      }
      confirmLabel={strings.assign.confirm}
      cancelLabel={strings.assign.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={confirm}
      onCancel={onClose}
      busy={busy}
      confirmDisabled={candidates.data === undefined}
    >
      <p className={styles.lead}>{strings.assign.lead(STAGE_LABEL[kit.stage])}</p>
      {working !== null && (
        <p className={styles.warning} role="status">
          {strings.assign.workingNow(working.userName)}
        </p>
      )}

      <div className={modalStyles.field}>
        <span className={modalStyles.label} id="assign-department">
          {strings.assign.department}
        </span>
        <div className={styles.departments} role="group" aria-labelledby="assign-department">
          {DESTINATIONS.map((stage) => (
            <button
              key={stage}
              type="button"
              aria-pressed={department === stage}
              className={`${styles.department} ${department === stage ? styles.on : ''}`}
              onClick={() => {
                setDepartment(stage);
                // A new department: its people, not the one chosen at the last.
                setChoice(stage === kit.stage ? kit.assignedToUserId : null);
                setSearch('');
              }}
            >
              {STAGE_LABEL[stage]}
              {stage === kit.stage && <span className={styles.current}>{strings.assign.hereNow}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className={modalStyles.field}>
        <span className={modalStyles.label}>{strings.assign.who}</span>
        <input
          type="search"
          className={styles.search}
          aria-label={strings.routeChoice.searchPeople}
          placeholder={strings.routeChoice.searchPeople}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <LeastBusyToggle on={leastBusy} onChange={setLeastBusy} />

        {candidates.isPending && <SkeletonRows rows={3} label={strings.assign.loading} />}
        {candidates.isError && <p className={modalStyles.error}>{strings.errors.server}</p>}

        {candidates.data !== undefined && (
          <div className={styles.people} role="group" aria-label={strings.assign.who}>
            <button
              type="button"
              className={`${styles.person} ${choice === null ? styles.on : ''}`}
              aria-pressed={choice === null}
              onClick={() => setChoice(null)}
            >
              <span className={styles.name}>{strings.assign.unassigned}</span>
              {kit.assignedToUserId === null && (
                <span className={styles.current}>{strings.assign.current}</span>
              )}
            </button>

            {listed.map((person) => (
              <button
                key={person.id}
                type="button"
                className={`${styles.person} ${choice === person.id ? styles.on : ''}`}
                aria-pressed={choice === person.id}
                onClick={() => setChoice(person.id)}
              >
                <AssigneeLine
                  name={person.name}
                  department={person.department}
                  activeCases={person.activeCases}
                />
                {kit.assignedToUserId === person.id && (
                  <span className={styles.current}>{strings.assign.current}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {choice !== null && (
        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor="assign-note">
            {strings.assign.note}
          </label>
          <textarea
            id="assign-note"
            className={modalStyles.textarea}
            value={note}
            placeholder={strings.assign.notePlaceholder}
            onChange={(event) => setNote(event.target.value)}
          />
          {note.trim() !== '' && (
            <div className={styles.visibility} role="radiogroup" aria-label={strings.assign.whoSees}>
              <label className={modalStyles.check}>
                <input
                  type="radio"
                  name="assign-visibility"
                  checked={visibility === 'everyone'}
                  onChange={() => setVisibility('everyone')}
                />
                {strings.assign.everyone}
              </label>
              <label className={modalStyles.check}>
                <input
                  type="radio"
                  name="assign-visibility"
                  checked={visibility === 'assignee'}
                  onChange={() => setVisibility('assignee')}
                />
                <span className={styles.private}>
                  <LockIcon size={13} />
                  {strings.assign.onlyAssignee}
                </span>
              </label>
              {visibility === 'assignee' && (
                <span className={modalStyles.hint}>{strings.assign.onlyAssigneeHint}</span>
              )}
            </div>
          )}
        </div>
      )}

      {reassigning && seesAll && privateNotes.length > 0 && (
        <div className={modalStyles.field}>
          <span className={modalStyles.label}>{strings.assign.forward}</span>
          <span className={modalStyles.hint}>{strings.assign.forwardHint}</span>
          {privateNotes.map((item) => (
            <label key={item.id} className={modalStyles.check}>
              <input
                type="checkbox"
                checked={forward.includes(item.id)}
                onChange={(event) =>
                  setForward((list) =>
                    event.target.checked ? [...list, item.id] : list.filter((id) => id !== item.id),
                  )
                }
              />
              <span>
                <b>{item.byUserName}</b>
                {item.visibleTo !== null && ` to ${item.visibleTo.name}`}: {item.text}
              </span>
            </label>
          ))}
        </div>
      )}

      {error !== null && (
        <p className={modalStyles.error} role="alert">
          {error}
        </p>
      )}
    </ConfirmSheet>
  );
}
